import { ALL_ORG_PERMISSIONS } from "./org-permissions.js";
//#region src/permission-key.ts
/**
* The ONE canonical app-permission KEY format: `<prefix>:<verb>` — exactly
* two lowercase segments joined by a single colon, no wildcards. Single
* source of truth for every place a permission key is validated: the DP
* grant DTO (`grant-app-permission.dto.ts`) and CP declaration ingest
* (`app-permission-declarations.service.ts`). Verbatim from the
* class-validator era; do NOT loosen — it is the anti-privilege-escalation
* gate (`*`, `prefix:*`, dot-form are all rejected).
*
* ⛔ THE PREFIX IS **NOT** CONSTRAINED TO THE APP SLUG, AND MUST NOT BE
* (#1968). This pattern is shape-only, and that is deliberate: live
* declarations on `dp-app` carry non-slug prefixes on their write/invoke/sync
* keys (`docs:write`, `agentdisc:sync`, …), so adding a
* `key.startsWith(appSlug + ':')` rule here would retro-invalidate keys that
* are currently declared AND currently granted. See
* {@link PERMISSION_KEY_MESSAGE} for why the message had to change instead.
*/
const PERMISSION_KEY_PATTERN = /^[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/;
/**
* Human-readable rejection message.
*
* ⛔ CORRECTED (#1968). This read *"permission key must match
* `<app-slug>:<verb>`"* — asserting a rule NOTHING enforces. The pattern
* never compared the first segment against the owning app's slug, so the
* message described a constraint that did not exist, in the one string a
* rejected app author reads. That mis-description has a measured cost: three
* app teams (`witness-docs-zion`, `saas-ai-agent-discovery-zion`,
* `demo-hub-zion`) renamed their `:read` key to `<slug>:read` believing the
* platform required it, which orphaned the live grants naming the old keys.
*
* ⛔ The FIX IS THE MESSAGE, NOT THE PATTERN — do not "restore consistency"
* by tightening the regex; see the note on {@link PERMISSION_KEY_PATTERN}.
* (The real forcing function for `<slug>:read` was
* `fixtures.controller.ts`'s hard-coded `${slug}:read` visibility probe, not
* this validator.)
*/
const PERMISSION_KEY_MESSAGE = "permission key must be two lowercase segments joined by a single colon (`<prefix>:<verb>`) — each segment starts with a letter and uses only a-z, 0-9 and `-`; no wildcards. The prefix need not equal the app slug";
/** True iff `key` matches the canonical two-segment `<prefix>:<verb>` format. */
function isValidPermissionKey(key) {
	return PERMISSION_KEY_PATTERN.test(key);
}
/**
* Reserved namespaces an APP permission KEY may never occupy. A key under
* `platform:*` or `org:*` is platform/org-plane vocabulary; granting OR
* declaring it as an "app permission" is the S-F1 escalation vector — an
* org-shaped app grant reaches the flat `orgPermissions` claim via the
* resolver's (still-present) explicit-path union
* (`principal-permission-resolver.service.ts:410`).
*/
const RESERVED_ORG_PERMISSION_PREFIXES = ["platform:", "org:"];
/**
* The two data-plane resource-grant org keys (dp-resource-grants Q10, R-030).
* `resource-grant:read` lets its holder list the grants on any resource of the
* org, and `resource-grant:manage` lets its holder create and revoke them.
* core-api registers both (`ALL_REGISTERED_ORG_PERMISSION_KEYS`) and reads
* them in `ResourceGrantWriterPolicy`. Neither is in `ORG_PERMISSION_CATALOG`,
* because the built-in `admin` role derives from that array and Q11 gives
* `admin` nothing by default, so they are listed here.
*
* Reserving them keeps an app from declaring either string as its own key:
* the CP declaration gate and the DP grant gate refuse it, and the principal
* resolver drops a legacy row that names it before the flat `orgPermissions`
* union. Without that, an app key of the same spelling would reach a machine
* principal's or an app token's `orgPermissions` and write grants on every
* resource in the org (S1c WS-3 security-1).
*
* The package barrel exports it (R-031), and core-api's `permission-catalog.ts`
* derives its two key constants from it, so each string has one home. The
* fixture test reads it too.
*/
const RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS = ["resource-grant:read", "resource-grant:manage"];
/**
* Every listed reserved org key: the org-admin catalog keys
* (`ALL_ORG_PERMISSIONS`: `settings:manage`, `users:read`, `fixtures:access`,
* …) and the two resource-grant keys. The catalog also holds three keys that
* start with the reserved prefix `org:`, so the prefix check reserves those
* three a second time. `cloud/platform-sdk/fixtures/reserved-org-permission-keys.json`
* mirrors this list for the Rust SDK. Module-level, outside the package barrel.
*/
const RESERVED_ORG_PERMISSION_KEY_LIST = [...ALL_ORG_PERMISSIONS, ...RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS];
/** O(1) membership of {@link RESERVED_ORG_PERMISSION_KEY_LIST}. */
const RESERVED_ORG_PERMISSION_KEYS = new Set(RESERVED_ORG_PERMISSION_KEY_LIST);
/**
* True iff `key` is RESERVED org/platform vocabulary that must NOT be usable
* as an APP permission — either it sits under a reserved namespace
* (`platform:*` / `org:*`) OR it is one of the listed keys
* ({@link RESERVED_ORG_PERMISSION_KEY_LIST}: the org-admin catalog keys and
* the two resource-grant keys). The SOLE reserved-vocabulary authority (S-F1),
* consumed by BOTH the DP grant-time gate (core-api) and the CP
* declaration-ingest gate (release-engine). A normal app key (`docs:read`)
* is NOT reserved.
*/
function isReservedOrgPermissionKey(key) {
	return RESERVED_ORG_PERMISSION_PREFIXES.some((p) => key.startsWith(p)) || RESERVED_ORG_PERMISSION_KEYS.has(key);
}
/** Human-readable rejection message for a reserved-key grant/declaration. */
const RESERVED_ORG_PERMISSION_MESSAGE = "permission key must not use a reserved org/platform namespace (`platform:*`, `org:*`) or collide with an org-admin permission key";
//#endregion
export { PERMISSION_KEY_MESSAGE, PERMISSION_KEY_PATTERN, RESERVED_ORG_PERMISSION_KEY_LIST, RESERVED_ORG_PERMISSION_MESSAGE, RESERVED_ORG_PERMISSION_PREFIXES, RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS, isReservedOrgPermissionKey, isValidPermissionKey };

//# sourceMappingURL=permission-key.js.map